/**
 * AppLoadContext.js - Global app data provider
 *
 * Home rails come from `GET /v1/home` (anonymous, shared). Continue watching / My List come
 * from `/v1/me/state` on the home page.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { api, unwrap } from '../api';
import { homeToCategories } from '../api/cards';

const AppLoadContext = createContext();
export const AppLoadProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetching = useRef(false);

  const fetchAppLoad = useCallback(async () => {
    // Outside the try on purpose: inside it, the early return still ran the
    // finally below, disarming the dedupe guard.
    if (fetching.current) {
      return;
    }
    fetching.current = true;
    try {
      setLoading(true);
      setError(null);
      const { data } = await unwrap(api.GET('/v1/home'));
      setCategories(homeToCategories(data));
    } catch (err) {
      console.error('Error fetching /v1/home:', err);
      setError("We couldn't load the app right now. Please refresh and try again.");
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppLoad();
  }, [fetchAppLoad]);

  const value = useMemo(
    () => ({ categories, loading, error, retry: fetchAppLoad }),
    [categories, loading, error, fetchAppLoad],
  );

  return <AppLoadContext.Provider value={value}>{children}</AppLoadContext.Provider>;
};

// Custom hook to use the context
export const useAppLoad = () => {
  const context = useContext(AppLoadContext);
  if (!context) {
    throw new Error('useAppLoad must be used within AppLoadProvider');
  }
  return context;
};
